import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { MeetingShareRow } from '@ai-meeting/shared'
import { fetchMeetingShares } from '@/services/meetings'
import type { SharedRoomMember } from '@/pages/Meetings/utils/sharedRoomPresentation'
import { normalizeMemberName } from '@/pages/Meetings/utils/normalizeMemberName'
import type { WaitingInviteMember } from '@/pages/Meetings/components/SharedRoom/SharedRoomMembersPopover'

/**
 * Host-side Invited/[Waiting] list for the live Members popover (BRAI-785).
 *
 * Merges the caller's active direct shares with WS room members by normalized
 * display name — the room frame has no userId. When a name appears in
 * `activeMembers`, that share drops out of Waiting without polling.
 */
export function useLiveInviteRoster(
  meetingId: string | null | undefined,
  activeMembers: readonly SharedRoomMember[]
) {
  const [shares, setShares] = useState<MeetingShareRow[]>([])
  const reloadSeqRef = useRef(0)

  // Load (and clear) whenever the live meeting changes so Waiting is ready
  // without requiring the host to open the Members popover first.
  useEffect(() => {
    let cancelled = false
    setShares([])
    if (!meetingId) {
      reloadSeqRef.current += 1
      return undefined
    }

    const seq = ++reloadSeqRef.current
    void (async () => {
      try {
        const data = await fetchMeetingShares(meetingId)
        if (cancelled || seq !== reloadSeqRef.current) return
        setShares(data.current)
      } catch {
        // Keep empty after a clear — transient failure must not invent rows.
      }
    })()

    return () => {
      cancelled = true
    }
  }, [meetingId])

  const reload = useCallback(async () => {
    if (!meetingId) {
      reloadSeqRef.current += 1
      setShares([])
      return
    }
    const seq = ++reloadSeqRef.current
    try {
      const data = await fetchMeetingShares(meetingId)
      if (seq !== reloadSeqRef.current) return
      setShares(data.current)
    } catch {
      // Keep the last good list — a transient shares failure must not blank
      // the Active roster which comes from WS independently.
    }
  }, [meetingId])

  const activeNames = useMemo(() => {
    const names = new Set<string>()
    for (const member of activeMembers) {
      names.add(normalizeMemberName(member.name))
    }
    return names
  }, [activeMembers])

  const waitingMembers: WaitingInviteMember[] = useMemo(
    () =>
      shares
        .filter((row) => !activeNames.has(normalizeMemberName(row.user.name)))
        .map((row) => ({ id: row.user.id, name: row.user.name })),
    [shares, activeNames]
  )

  return { waitingMembers, reload }
}
